import React from "react";
import { Button, Divider, Select, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { ResourceType } from "../../../../types";
import type { ResourceResponse } from "../../../../types/api/getResources";
import { resolveResourceType } from "../../LessonForm/utils";
import SortableList from "../../../SortableList";

const { Title } = Typography;
const { Option } = Select;

export interface CourseResourceRow {
  resource_id: number | null;
  order_by: number;
  resourceType: ResourceType;
  // Stable per-row id for drag-and-drop, kept out of the submit payload. It
  // survives edits (which replace the row object via spread), so selecting a
  // resource/type does not remount the sortable row.
  _uid?: string;
}

let resourceRowSeq = 0;
export const nextResourceRowUid = () =>
  `course-resource-row-${(resourceRowSeq += 1)}`;

interface CourseResourceSelectorProps {
  items: CourseResourceRow[];
  resources: ResourceResponse[];
  onChange: (items: CourseResourceRow[]) => void;
}

const CourseResourceSelector: React.FC<CourseResourceSelectorProps> = ({
  items,
  resources,
  onChange,
}) => {
  // Rows render in array order and order_by mirrors that index end-to-end, so
  // every mutation operates on `items` directly (by rendered position) and then
  // renumbers order_by from the index — never re-sorting first, which would
  // desync the rendered position from the index the handlers receive.
  const renumber = (rows: CourseResourceRow[]) =>
    rows.map((row, index) => ({ ...row, order_by: index }));

  const handleAdd = () => {
    onChange([
      ...renumber(items),
      {
        resource_id: null,
        order_by: items.length,
        resourceType: ResourceType.VIDEO,
        _uid: nextResourceRowUid(),
      },
    ]);
  };

  const handleRemove = (index: number) => {
    onChange(renumber(items.filter((_, i) => i !== index)));
  };

  const handleTypeChange = (index: number, type: ResourceType) => {
    const updated = [...items];
    updated[index] = { ...updated[index], resourceType: type, resource_id: null };
    onChange(updated);
  };

  const handleResourceChange = (index: number, resourceId: number | null) => {
    const updated = [...items];
    updated[index] = { ...updated[index], resource_id: resourceId };
    onChange(updated);
  };

  const handleReorder = (reordered: CourseResourceRow[]) => {
    // The carousel order is the array index (order_by mirrors it end-to-end),
    // so renumber after the drop to keep both in sync.
    onChange(reordered.map((row, index) => ({ ...row, order_by: index })));
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <Divider />
      <Title level={4}>Recursos del curso (carrusel)</Title>
      <p style={{ color: "#8c8c8c", marginBottom: 16 }}>
        Estos recursos se muestran en el carrusel del curso, sin necesidad de asociarlos a una lección.
      </p>

      <SortableList
        items={items}
        onReorder={handleReorder}
        getId={(item) => item._uid ?? `resource-${item.resource_id ?? "new"}`}
        renderItem={(item, index, { dragHandle }) => (
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            marginBottom: 12,
            padding: "12px 16px",
            background: "var(--bo-inset-bg)",
            border: "1px solid var(--bo-card-border)",
            borderRadius: 6,
          }}
        >
          {dragHandle}

          <span style={{ minWidth: 20, color: "#8c8c8c", fontWeight: 600 }}>
            {index + 1}.
          </span>

          <Select
            style={{ width: 200 }}
            value={item.resourceType}
            onChange={(value) => handleTypeChange(index, value)}
          >
            {Object.values(ResourceType).map((type) => (
              <Option key={type} value={type}>
                {resolveResourceType(type)}
              </Option>
            ))}
          </Select>

          <Select
            style={{ flex: 1 }}
            placeholder="Selecciona un recurso"
            value={item.resource_id ?? undefined}
            onChange={(value) => handleResourceChange(index, value ?? null)}
            showSearch
            filterOption={(input, option) =>
              String(option?.children ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            allowClear
            onClear={() => handleResourceChange(index, null)}
          >
            {resources
              .filter((r) => r.type === item.resourceType)
              .map((r) => (
                <Option key={r.id} value={r.id}>
                  {`Recurso ${r.id} - ${r.title}`}
                </Option>
              ))}
          </Select>

          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleRemove(index)}
            title="Eliminar recurso"
          />
        </div>
        )}
      />

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={handleAdd}
        style={{ marginTop: 8 }}
      >
        Agregar recurso al curso
      </Button>
    </div>
  );
};

export default CourseResourceSelector;
